export const formatLocalTime = (timezone) => {
  if (!timezone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date());
  } catch {
    return null;
  }
};

export const formatTimezoneAbbr = (timezone) => {
  if (!timezone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
  } catch {
    return null;
  }
};

export const formatScheduledAt = (isoString, timezone) => {
  if (!isoString) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(isoString));
  } catch {
    return null;
  }
};

export const getLocalHour = (timezone) => {
  if (!timezone) return null;
  try {
    return parseInt(
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hour12: false,
      }).format(new Date()),
      10
    );
  } catch {
    return null;
  }
};

export const isWithinCallHours = (timezone) => {
  const hour = getLocalHour(timezone);
  if (hour === null) return true;
  return hour >= 8 && hour < 20;
};

// Converts a date string ("YYYY-MM-DD") and hour (0-23) in a given IANA timezone
// to the equivalent UTC Date object. Handles fractional-hour offsets (e.g. India UTC+5:30).
export const mentorHourToUTC = (mentorDateStr, hour, timezone) => {
  if (!timezone) return new Date(NaN);
  const [year, month, day] = mentorDateStr.split('-').map(Number);

  // Treat the target date+hour as if it were UTC to get a starting point
  const fakeUTC = new Date(Date.UTC(year, month - 1, day, hour, 0, 0));

  // Find out what fakeUTC looks like when displayed in the target timezone
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(fakeUTC)
      .map(({ type, value }) => [type, value])
  );

  // Some browsers return "24" for midnight in 24h mode
  const displayedHour = parseInt(parts.hour, 10) % 24;
  const displayedMinute = parseInt(parts.minute, 10);

  // Shift fakeUTC so it lands on the desired local hour:00
  const diffMinutes = hour * 60 - (displayedHour * 60 + displayedMinute);
  return new Date(fakeUTC.getTime() + diffMinutes * 60 * 1000);
};

const SLOT_DURATION_MS = 60 * 60 * 1000; // 1 hour

// Generates all selectable one-hour slots within [24h from now, 14 days from now],
// restricted to 8 AM–7 PM in the slot-owner's timezone. Slots are grouped by calendar date.
export const generateAvailabilitySlots = (slotTimezone) => {
  const tz = slotTimezone || 'UTC';
  const now = new Date();
  const minStart = new Date(now.getTime() + 24 * 3600 * 1000);
  const maxStart = new Date(now.getTime() + 14 * 24 * 3600 * 1000);

  const todayInTz = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(now);
  const [baseYear, baseMonth, baseDay] = todayInTz.split('-').map(Number);
  const baseDate = new Date(Date.UTC(baseYear, baseMonth - 1, baseDay));

  const dayMap = new Map();
  const msPerDay = 24 * 60 * 60 * 1000;

  for (let daysAhead = 1; daysAhead <= 14; daysAhead++) {
    const targetDate = new Date(baseDate.getTime() + daysAhead * msPerDay);
    const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(targetDate);

    for (let hour = 8; hour <= 19; hour++) {
      const startUTC = mentorHourToUTC(dateStr, hour, tz);
      if (isNaN(startUTC) || startUTC < minStart || startUTC >= maxStart) continue;

      const endUTC = new Date(startUTC.getTime() + SLOT_DURATION_MS);
      if (endUTC >= maxStart) continue;

      const dateInTz = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(startUTC);
      const [y, m, d] = dateInTz.split('-').map(Number);
      const localDateKey = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

      if (!dayMap.has(localDateKey)) {
        dayMap.set(localDateKey, {
          key: localDateKey,
          dayName: new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(startUTC),
          dateStr: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(startUTC),
          slots: [],
        });
      }

      dayMap.get(localDateKey).slots.push({
        key: startUTC.toISOString(),
        start_at: startUTC.toISOString(),
        end_at: endUTC.toISOString(),
        displayTime: new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(startUTC),
      });
    }
  }

  return {
    days: Array.from(dayMap.values()).sort((a, b) => a.key.localeCompare(b.key)),
  };
};

// Splits each one-hour proposed slot into two thirty-minute options for the acceptor to choose from.
export const expandSlotsToThirtyMinuteChoices = (proposedSlots) => {
  if (!proposedSlots?.length) return [];
  const choices = [];
  for (const slot of proposedSlots) {
    const start = new Date(slot.start_at);
    const end = new Date(slot.end_at);
    const durationMs = end - start;
    if (durationMs < 30 * 60 * 1000) continue; // skip if shorter than 30 min
    // First half: start -> start+30min
    choices.push({ start_at: slot.start_at, end_at: new Date(start.getTime() + 30 * 60 * 1000).toISOString() });
    if (durationMs >= 60 * 60 * 1000) {
      // Second half: start+30min -> end
      choices.push({
        start_at: new Date(start.getTime() + 30 * 60 * 1000).toISOString(),
        end_at: slot.end_at,
      });
    }
  }
  return choices;
};

export const formatSlotInTimezone = (slot, timezone) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(slot.start_at));
};

export const formatTimeRangeInTimezone = (startISO, endISO, timezone, tzAbbr) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const opts = { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true };
  const fmt = (iso) => new Intl.DateTimeFormat('en-US', opts).format(new Date(iso));
  return `${fmt(startISO)} – ${fmt(endISO)}${tzAbbr ? ` (${tzAbbr})` : ''}`;
};

export const formatSlotRangeInTimezone = (startISO, endISO, timezone, tzAbbr) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateOpts = { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' };
  const timeOpts = { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true };
  const dateStr = new Intl.DateTimeFormat('en-US', dateOpts).format(new Date(startISO));
  const timeStr = formatTimeRangeInTimezone(startISO, endISO, tz, tzAbbr);
  return `${dateStr}, ${timeStr}`;
};

const formatMentorHourInViewerTimezone = (mentorTimezone, mentorHour) => {
  if (!mentorTimezone) return null;
  try {
    const now = new Date();
    const mentorDateStr = new Intl.DateTimeFormat('en-CA', { timeZone: mentorTimezone }).format(now);
    const utc = mentorHourToUTC(mentorDateStr, mentorHour, mentorTimezone);
    if (isNaN(utc)) return null;

    const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Intl.DateTimeFormat('en-US', {
      timeZone: viewerTz,
      hour: 'numeric',
      hour12: true,
    }).format(utc);
  } catch {
    return null;
  }
};

export const formatCallStartInViewerTimezone = (mentorTimezone) => {
  const hour = formatMentorHourInViewerTimezone(mentorTimezone, 8);
  if (!hour) return null;
  const abbr = formatTimezoneAbbr(Intl.DateTimeFormat().resolvedOptions().timeZone);
  return `${hour}${abbr ? ` ${abbr}` : ''}`;
};

export const formatCallHoursRangeInViewerTimezone = (mentorTimezone) => {
  const startHour = formatMentorHourInViewerTimezone(mentorTimezone, 8);
  const endHour = formatMentorHourInViewerTimezone(mentorTimezone, 20);
  if (!startHour || !endHour) return null;
  const abbr = formatTimezoneAbbr(Intl.DateTimeFormat().resolvedOptions().timeZone);
  return { startHour, endHour, abbr };
};

export const getTimeOfDayNote = (timezone, firstName) => {
  const hour = getLocalHour(timezone);
  if (hour === null) return null;
  const cap = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : firstName;

  if (hour >= 8 && hour < 11) return `The birds are chirping in ${firstName}'s timezone, a perfect time to call!`;
  if (hour >= 11 && hour < 14) return `${cap} is probably in their groove right now, great time to connect!`;
  if (hour >= 14 && hour < 17) return `The afternoon lull is real, ${firstName} might love the distraction!`;
  if (hour >= 17 && hour < 20) return `${cap} is winding down, a cozy time for a good conversation!`;
  return `It's pretty late for ${firstName}! Scheduling might give you better luck!`;
};
